<template>
  <div>
    <!-- download button aligned at the end -->
    <b-container fluid class="mt-3">
      <b-row class="align-items-center">
        <b-col class="d-flex align-items-center">
          <span class="records-count">
            {{ totalRecords }} record{{ totalRecords === 1 ? "" : "s" }} found
          </span>
        </b-col>
        <b-col class="text-right">
          <!--           <b-button
            variant="outline-primary"
            @click="showUploadModal"
            class="float-left mr-2"
          >
            Upload XLS <b-icon icon="upload" />
          </b-button> -->
          <b-button
            :disabled="
              vehicleValuationXLSIsLoading ||
              isGenerationLoading ||
              xlsTimerIsLoading
            "
            variant="outline-success"
            @click="downloadVehicleValuation"
          >
            Download XLS <b-icon icon="file-spreadsheet" />
            <!-- loader -->
            <b-spinner
              v-if="
                vehicleValuationXLSIsLoading ||
                isGenerationLoading ||
                xlsTimerIsLoading
              "
              small
              label="Loading..."
            />
          </b-button>
        </b-col>
      </b-row>
    </b-container>

    <!-- Upload Modal -->
    <b-modal
      id="upload-xls-modal"
      title="Upload XLS File"
      hide-footer
      centered
      size="lg"
    >
      <b-form @submit.prevent="uploadFile">
        <b-form-group label="Select Provider:">
          <vue-multiselect
            v-model="selectedProvider"
            :options="providers"
            :searchable="true"
            :close-on-select="true"
            :show-labels="false"
            placeholder="Select a provider"
            track-by="id"
            label="name"
          />
        </b-form-group>

        <b-form-group label="Select XLS File:">
          <b-form-file
            v-model="xlsFile"
            :state="Boolean(xlsFile)"
            placeholder="Choose a file or drop it here..."
            drop-placeholder="Drop file here..."
            accept=".xls,.xlsx"
          ></b-form-file>
          <b-form-text> Only XLS/XLSX files are accepted </b-form-text>
        </b-form-group>

        <div class="d-flex justify-content-end mt-3">
          <b-button
            variant="secondary"
            class="mr-2"
            @click="$bvModal.hide('upload-xls-modal')"
          >
            Cancel
          </b-button>
          <b-button
            variant="success"
            type="submit"
            :disabled="!isFormValid || isUploading"
          >
            Upload
            <b-spinner
              v-if="isUploading"
              small
              label="Uploading..."
            ></b-spinner>
          </b-button>
        </div>
      </b-form>
    </b-modal>

    <!-- Rest of the template -->
    <div class="pt-4">
      <div class="table-responsive">
        <b-table
          show-empty
          bordered
          hover
          striped
          stacked="sm"
          head-variant="light"
          :items="vehicleValuation.data ? vehicleValuation.data : []"
          :fields="fields"
          :busy="vehicleValuationStatus.status === 'fetching'"
          class="valuation-table"
        >
          <template v-slot:cell(vehicle)="{ item }">
            <div class="grouped-cell">
              <div class="grouped-row">
                <span class="grouped-label">VIN:</span>
                <span
                  class="grouped-value cursor-pointer text-primary"
                  @click="redirectToVehicleValuationDetail(item)"
                >
                  {{ item.vin }}
                </span>
              </div>
              <div class="grouped-row grouped-row-stacked">
                <span class="grouped-label">Description:</span>
                <span class="grouped-value">{{ item.description }}</span>
              </div>
              <div class="grouped-row">
                <span class="grouped-label">FX:</span>
                <span class="grouped-value">{{ item.fx }}</span>
              </div>
              <div class="grouped-row">
                <span class="grouped-label">Region:</span>
                <span class="grouped-value">{{ item.region }}</span>
              </div>
              <div class="grouped-row grouped-row-actions">
                <b-button
                  size="sm"
                  variant="outline-success"
                  :disabled="vehicleValuationXLSIsLoading || isGenerationLoading"
                  @click="downloadRowXLS(item)"
                >
                  <template v-if="downloadingRowId === item.id">
                    <b-spinner small label="Loading..." />
                  </template>
                  <template v-else> XLS <b-icon icon="file-spreadsheet" /> </template>
                </b-button>
              </div>
            </div>
          </template>

          <template v-slot:cell(acquisition)="{ item }">
            <div class="grouped-cell">
              <div class="grouped-row">
                <span class="grouped-label">Date:</span>
                <span class="grouped-value">{{ item.acquisitionDate }}</span>
              </div>
              <div class="grouped-row">
                <span class="grouped-label">Cost:</span>
                <span class="grouped-value">{{
                  item.acquisitionCost | currency
                }}</span>
              </div>
            </div>
          </template>

          <template v-slot:cell(current)="{ item }">
            <div class="grouped-cell">
              <div class="grouped-row">
                <span class="grouped-label">Date:</span>
                <span class="grouped-value">{{ item.today }}</span>
              </div>
              <div class="grouped-row">
                <span class="grouped-label">Avg Grade:</span>
                <span class="grouped-value">{{ item.averageGrade }}</span>
              </div>
              <div class="grouped-row">
                <span class="grouped-label">Avg Miles:</span>
                <span class="grouped-value">{{ item.averageMiles }}</span>
              </div>
              <div class="grouped-row grouped-row-stacked">
                <span class="grouped-label">MMR Range:</span>
                <span class="grouped-value"
                  >{{ item.costTodayBelow | currency }} -
                  {{ item.costTodayAbove | currency }}</span
                >
              </div>
              <div class="grouped-row">
                <span class="grouped-label">MMR Avg:</span>
                <span class="grouped-value">{{
                  item.costTodayAvg | currency
                }}</span>
              </div>
            </div>
          </template>

          <template v-slot:cell(forecast3M)="{ item }">
            <div class="grouped-cell">
              <div class="grouped-row">
                <span class="grouped-label">Date:</span>
                <span class="grouped-value">{{ item.forecasted3Months }}</span>
              </div>
              <div class="grouped-row grouped-row-stacked">
                <span class="grouped-label">Range:</span>
                <span class="grouped-value"
                  >{{ item.costForecasted3MonthsBelow | currency }} -
                  {{ item.costForecasted3MonthsAbove | currency }}</span
                >
              </div>
              <div class="grouped-row">
                <span class="grouped-label">Avg:</span>
                <span class="grouped-value">{{
                  item.costForecasted3MonthsAvg | currency
                }}</span>
              </div>
            </div>
          </template>

          <template v-slot:cell(forecast6M)="{ item }">
            <div class="grouped-cell">
              <div class="grouped-row">
                <span class="grouped-label">Date:</span>
                <span class="grouped-value">{{ item.forecasted6Months }}</span>
              </div>
              <div class="grouped-row grouped-row-stacked">
                <span class="grouped-label">Range:</span>
                <span class="grouped-value"
                  >{{ item.costForecasted6MonthsBelow | currency }} -
                  {{ item.costForecasted6MonthsAbove | currency }}</span
                >
              </div>
              <div class="grouped-row">
                <span class="grouped-label">Avg:</span>
                <span class="grouped-value">{{
                  item.costForecasted6MonthsAvg | currency
                }}</span>
              </div>
            </div>
          </template>

          <template v-slot:cell(forecast9M)="{ item }">
            <div class="grouped-cell">
              <div class="grouped-row">
                <span class="grouped-label">Date:</span>
                <span class="grouped-value">{{ item.forecasted9Months }}</span>
              </div>
              <div class="grouped-row grouped-row-stacked">
                <span class="grouped-label">Range:</span>
                <span class="grouped-value"
                  >{{ item.costForecasted9MonthsBelow | currency }} -
                  {{ item.costForecasted9MonthsAbove | currency }}</span
                >
              </div>
              <div class="grouped-row">
                <span class="grouped-label">Avg:</span>
                <span class="grouped-value">{{
                  item.costForecasted9MonthsAvg | currency
                }}</span>
              </div>
            </div>
          </template>

          <template v-slot:cell(forecast12M)="{ item }">
            <div class="grouped-cell">
              <div class="grouped-row">
                <span class="grouped-label">Date:</span>
                <span class="grouped-value">{{
                  item.forecasted12Months
                }}</span>
              </div>
              <div class="grouped-row grouped-row-stacked">
                <span class="grouped-label">Range:</span>
                <span class="grouped-value"
                  >{{ item.costForecasted12MonthsBelow | currency }} -
                  {{ item.costForecasted12MonthsAbove | currency }}</span
                >
              </div>
              <div class="grouped-row">
                <span class="grouped-label">Avg:</span>
                <span class="grouped-value">{{
                  item.costForecasted12MonthsAvg | currency
                }}</span>
              </div>
            </div>
          </template>

          <template v-slot:cell(ago6M)="{ item }">
            <div class="grouped-cell">
              <div class="grouped-row">
                <span class="grouped-label">Date:</span>
                <span class="grouped-value">{{ item.date6MonthsAgo }}</span>
              </div>
              <div class="grouped-row grouped-row-stacked">
                <span class="grouped-label">Range:</span>
                <span class="grouped-value"
                  >{{ item.cost6MonthsAgoBelow | currency }} -
                  {{ item.cost6MonthsAgoAbove | currency }}</span
                >
              </div>
              <div class="grouped-row">
                <span class="grouped-label">Avg:</span>
                <span class="grouped-value">{{
                  item.cost6MonthsAgoAvg | currency
                }}</span>
              </div>
            </div>
          </template>

          <template v-slot:cell(ago12M)="{ item }">
            <div class="grouped-cell">
              <div class="grouped-row">
                <span class="grouped-label">Date:</span>
                <span class="grouped-value">{{ item.date12MonthsAgo }}</span>
              </div>
              <div class="grouped-row grouped-row-stacked">
                <span class="grouped-label">Range:</span>
                <span class="grouped-value"
                  >{{ item.cost12MonthsAgoBelow | currency }} -
                  {{ item.cost12MonthsAgoAbove | currency }}</span
                >
              </div>
              <div class="grouped-row">
                <span class="grouped-label">Avg:</span>
                <span class="grouped-value">{{
                  item.cost12MonthsAgoAvg | currency
                }}</span>
              </div>
            </div>
          </template>
        </b-table>
      </div>
      <b-row>
        <b-col
          xs="12"
          sm="12"
          md="12"
          lg="12"
          xl="12"
          class="d-flex justify-content-end"
        >
          <b-pagination
            :value="vehicleValuation ? vehicleValuation.currentPage : 1"
            :total-rows="vehicleValuation ? vehicleValuation.total : 0"
            :per-page="vehicleValuation ? vehicleValuation.perPage : 0"
            class="mb-2"
            @change="changePage($event)"
          />
        </b-col>
      </b-row>
    </div>
  </div>
</template>
<script>
import { mapState } from "vuex";
import {
  GET_VEHICLE_VALUATION,
  GET_VEHICLE_VALUATION_XLS,
  SET_VEHICLE_VALUATION_FILTERS,
  UPLOAD_VEHICLE_VALUATION_XLS,
} from "./actions";
import { GET_SUPPLIERS_LIST } from "../vehicles-provider-price/actions";
import VueMultiselect from "vue-multiselect";

export default {
  name: "VehicleValuationDetail",
  components: {
    VueMultiselect,
  },
  computed: {
    ...mapState({
      vehicleValuation: (state) =>
        state.adminStore.vehicleValuation.vehicleValuations.data,
      vehicleValuationStatus: (state) =>
        state.adminStore.vehicleValuation.vehicleValuations.status,
      vehicleValuationXLSIsLoading: (state) =>
        state.adminStore.vehicleValuation.vehicleValuationXLS.status ===
        "fetching",
      providersListData: (state) =>
        state.adminStore.vehiclesProviderPrice.providersList.data,

      isProvidersLoading: (state) =>
        state.adminStore.vehiclesProviderPrice.providersList.status ===
        "fetching",
      isGenerationLoading: (state) =>
        state.adminStore.vehicleValuation.vehicleValuationDetail.status ==
        "fetching",
    }),
    isFormValid() {
      return this.selectedProvider && this.xlsFile;
    },
    providers() {
      return this.providersListData || [];
    },
    totalRecords() {
      return this.vehicleValuation ? this.vehicleValuation.total || 0 : 0;
    },
  },
  watch: {
    /* wait 10 second after isGenerationLoading changes */
    isGenerationLoading(newValue) {
      if (newValue) {
        this.xlsTimerIsLoading = true;
        setTimeout(() => {
          this.xlsTimerIsLoading = false;
        }, 10000);
      }
    },
  },
  data() {
    return {
      fields: [
        {
          key: "vehicle",
          label: "Vehicle",
          thClass: "vehicle-column",
        },
        {
          key: "acquisition",
          label: "Acquisition",
          thClass: "group-column",
        },
        {
          key: "current",
          label: "Current (MMR)",
          thClass: "group-column-wide",
        },
        {
          key: "ago6M",
          label: "6 Months Ago",
          thClass: "group-column",
        },
        {
          key: "ago12M",
          label: "12 Months Ago",
          thClass: "group-column",
        },
        {
          key: "forecast3M",
          label: "3 Months",
          thClass: "group-column",
        },
        {
          key: "forecast6M",
          label: "6 Months",
          thClass: "group-column",
        },
        {
          key: "forecast9M",
          label: "9 Months",
          thClass: "group-column",
        },
        {
          key: "forecast12M",
          label: "12 Months",
          thClass: "group-column",
        },
      ],
      selectedProvider: null,
      xlsFile: null,
      isUploading: false,
      xlsTimerIsLoading: false,
      downloadingRowId: null,
    };
  },
  methods: {
    showUploadModal() {
      // Load providers list if not already loaded
      if (!this.providers.length && !this.isProvidersLoading) {
        this.loadProviders();
      }
      this.$bvModal.show("upload-xls-modal");
    },

    loadProviders() {
      this.$store
        .dispatch(GET_SUPPLIERS_LIST)
        .then(() => {})
        .catch((error) => {
          console.error("Error loading providers:", error);
          this.$bvToast.toast("Failed to load providers", {
            title: "Error",
            variant: "danger",
            solid: true,
          });
        });
    },

    uploadFile() {
      if (!this.selectedProvider || !this.xlsFile) {
        this.$bvToast.toast("Please select provider and file", {
          title: "Error",
          variant: "danger",
          solid: true,
        });
        return;
      }

      this.isUploading = true;

      const formData = new FormData();
      formData.append("id", this.selectedProvider.id);
      formData.append("file", this.xlsFile);

      // Simulate upload for now - replace with actual API call
      this.$store
        .dispatch(UPLOAD_VEHICLE_VALUATION_XLS, formData)
        .then((response) => {
          console.log("File uploaded successfully:", response);
          this.$root.$bvToast.toast("File uploaded successfully", {
            title: "Success",
            variant: "success",
            solid: true,
          });
          this.resetUploadForm();
          this.$store.dispatch(GET_VEHICLE_VALUATION);
        })
        .catch((error) => {
          console.error("Error uploading file:", error);
          this.$root.$bvToast.toast("Failed to upload file", {
            title: "Error",
            variant: "danger",
            solid: true,
          });
        })
        .finally(() => {
          this.isUploading = false;
        });
    },

    resetUploadForm() {
      this.selectedProvider = null;
      this.xlsFile = null;
      this.isUploading = false;
    },

    downloadVehicleValuation() {
      /* wait 10 seconds before next action */
      this.xlsTimerIsLoading = true;
      setTimeout(() => {
        this.$store.dispatch(GET_VEHICLE_VALUATION_XLS, {}).then((response) => {
          const blob = new Blob([response], {
            type: "application/vnd.ms-excel",
          });
          const link = document.createElement("a");
          link.href = window.URL.createObjectURL(blob);
          link.download =
            this.formatFileName("Manheim Valuation") + ".xls" || "ficher.xls";
          link.click();
        });
        this.xlsTimerIsLoading = false;
      }, 10000);
    },
    downloadRowXLS(item) {
      this.downloadingRowId = item.id;
      this.$store.commit(SET_VEHICLE_VALUATION_FILTERS, {
        reset: false,
        fields: { id: item.id },
      });
      this.$store
        .dispatch(GET_VEHICLE_VALUATION_XLS, {})
        .then((response) => {
          const blob = new Blob([response], {
            type: "application/vnd.ms-excel",
          });
          const link = document.createElement("a");
          link.href = window.URL.createObjectURL(blob);
          link.download =
            this.formatFileName(`Manheim Valuation - ${item.vin}`) + ".xls";
          link.click();
        })
        .catch((error) => {
          console.error("Error downloading vehicle valuation:", error);
          this.$root.$bvToast.toast("Failed to download XLS", {
            title: "Error",
            variant: "danger",
            solid: true,
          });
        })
        .finally(() => {
          this.$store.commit(SET_VEHICLE_VALUATION_FILTERS, {
            reset: false,
            fields: { id: null },
          });
          this.downloadingRowId = null;
        });
    },

    formatFileName(type) {
      const date = new Date();
      const day = date.getDate().toString().padStart(2, "0");
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const year = date.getFullYear();
      const hours = date.getHours().toString().padStart(2, "0");
      const minutes = date.getMinutes().toString().padStart(2, "0");

      return `${type}-${year}-${month}-${day}-${hours}${minutes}`;
    },

    changePage(page) {
      this.$store.commit(SET_VEHICLE_VALUATION_FILTERS, {
        reset: false,
        fields: {
          page,
        },
      });
      this.$store.dispatch(GET_VEHICLE_VALUATION).then((response) => {
        console.log(response);
      });
    },

    redirectToVehicleValuationDetail(item) {
      console.log("Row clicked:", item);
      const vinNumber = item.vin;

      this.$router.push({
        name: "admin.vehicles-price",
        params: { vinNumber: vinNumber },
      });
    },
  },
};
</script>

<style>
.records-count {
  font-weight: 600;
  color: #495057;
}

.valuation-table {
  width: 100%;
  table-layout: fixed;
  font-size: 0.85rem;
}

.valuation-table th,
.valuation-table td {
  padding: 0.5rem 0.6rem !important;
  vertical-align: top;
}

.valuation-table th {
  font-weight: 600;
  text-align: center;
}

/* Column-specific widths (vehicle + acquisition + current + 6 group columns = 100%) */
.vehicle-column {
  width: 16%;
}

.group-column {
  width: 10%;
}

.group-column-wide {
  width: 13%;
}

/* Grouped cell: stacked label/value rows instead of separate columns */
.grouped-cell {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.grouped-row {
  display: flex;
  gap: 4px;
  line-height: 1.3;
}

.grouped-row-stacked {
  display: flex;
  flex-direction: column;
  gap: 0;
  line-height: 1.3;
}

.grouped-label {
  font-weight: 600;
  color: #6c757d;
  white-space: nowrap;
}

.grouped-value {
  word-break: break-word;
}

.grouped-row-actions {
  margin-top: 4px;
}

/* Make sure the table can still scroll horizontally as a fallback on very small screens */
.table-responsive {
  overflow-x: auto;
  min-height: 0.01%;
  margin-bottom: 1rem;
  width: 100%;
}

@media screen and (max-width: 991px) {
  .valuation-table {
    table-layout: auto;
  }

  .vehicle-column,
  .group-column,
  .group-column-wide {
    width: auto;
    min-width: 220px;
  }
}
</style>
